<script lang="ts" setup>
import { PopoverPositionEnum, TirPmColorPalette, TirPmInput, TirPmScrollbar } from "tir-components";
import {
  TaskParameterModel,
  TaskParameterTypeEnum,
  TaskParameter,
  getTaskParameterPalette,
  ParametersTemplateModel,
} from "@/components";
import { computed, onMounted, onUnmounted, PropType, ref } from "vue";
import { PlusIcon } from "tir-style-system/icons/outline";
import { PaintIcon } from "tir-style-system/icons/solid";

/**Параметры */
const props = defineProps({
  /**Тип настройки */
  type: { type: Number as PropType<TaskParameterTypeEnum> },
  /**Демо параметр для поля ввода */
  upsertParameter: {
    type: Object as PropType<TaskParameterModel>,
    default: () => new TaskParameterModel(),
  },
  /**Список параметров текущей сущности */
  currentParameters: { type: Array as PropType<TaskParameterModel[]> },
  /**Список игнорируемых параметров */
  ignoreParameters: { type: Array as PropType<TaskParameterModel[]> },
  /**Список шаблонов */
  templates: { type: Array as PropType<ParametersTemplateModel[]> },
  /**Заголовок параметра */
  title: { type: String, default: "" },
  /**Спрятать вывод шаблонов */
  hideTemplates: { type: Boolean, default: false },
});
/**События */
const emit = defineEmits<{
  /**Изменение добавляемого/редактируемого параметра */
  (e: "update:upsertParameter", parameter: TaskParameterModel);
  /**Создать/Редактировать параметр */
  (e: "upsert", parameter: TaskParameterModel);
  /**Выбор шаблона */
  (e: "selectTemplate", template: ParametersTemplateModel);
}>();

/**Сфокусировано ли поле ввода */
const isFocused = ref(false);
/**Элемент обертки */
const $wrapper = ref<HTMLElement>();
/**Стили выпадающего списка */
const dropdownStyle = ref();

/**Общий список параметров шаблонов для поиска */
const templateParameters = computed(() => {
  let parameters = [];
  props.templates?.forEach((template) => {
    parameters.push(...template.ParsedData);
  });
  return parameters;
});
/**Виден ли выпадающий список */
const isDropdownVisible = computed(() => {
  return (
    (!!searchedParameters.value.length || !!searchedTemplates.value.length) &&
    isFocused.value &&
    props.upsertParameter.Name
  );
});
/**Все параметры */
const allParameters = computed(() => {
  const current = props.currentParameters || [];
  const all = [...current, ...templateParameters.value];

  const uniqueParameters = Object.values(
    all.reduce((acc, obj) => ({ ...acc, [obj.Name.toLocaleLowerCase()]: obj }), {}),
  ) as TaskParameterModel[];

  if (!props.ignoreParameters?.length) return uniqueParameters;

  return uniqueParameters.filter(
    (p) =>
      !props.ignoreParameters.some((x) => x.Name.toLocaleLowerCase() == p.Name.toLocaleLowerCase()),
  );
});
/**Найденные параметры */
const searchedParameters = computed(() => {
  const name = props.upsertParameter.Name.toLocaleLowerCase();
  if (!name) return [];
  const searched = allParameters.value.filter((parameter: TaskParameterModel) => {
    return parameter.Name.toLocaleLowerCase().includes(name);
  });
  return searched;
});
/**Найденные шаблоны */
const searchedTemplates = computed(() => {
  const name = props.upsertParameter.Name.toLocaleLowerCase();
  if (!name) return [];
  const searched = props.templates.filter((template) => {
    return (
      template.Name.toLocaleLowerCase().includes(name) ||
      template.ParsedData.some((parameter) => parameter.Name.toLocaleLowerCase().includes(name))
    );
  });
  return searched;
});
/**Обновление компонента по типу */
const label = computed(() => {
  let text = "Введите название ";

  switch (props.type) {
    case TaskParameterTypeEnum.Status:
      text += "статуса";
      break;
    case TaskParameterTypeEnum.Priority:
      text += "приоритета";
      break;
    case TaskParameterTypeEnum.Tag:
      text += "тега";
      break;
    case TaskParameterTypeEnum.Type:
      text += "типа";
      break;
    case TaskParameterTypeEnum.Difficulty:
      text += "сложности";
      break;
  }

  return text;
});
/**Запрещено добавление параметра */
const isCreateDisabled = computed(() => {
  const ignored = props.ignoreParameters || [];
  const parameters = [...ignored, ...searchedParameters.value];
  return parameters.some(
    (p) => p.Name.toLocaleLowerCase() == props.upsertParameter.Name.toLocaleLowerCase(),
  );
});
/**Классы кнопки добавления параметра */
const addButtonClasses = computed(() => ({
  disabled: isCreateDisabled.value,
}));
/**rem единица */
const rem = computed(() => parseFloat(getComputedStyle(document.documentElement).fontSize));

/**После рендра */
onMounted(() => {
  window.addEventListener("resize", setDropdownStyle);
  setTimeout(() => setDropdownStyle());
});
/**Перед удалением компонента */
onUnmounted(() => {
  window.removeEventListener("resize", setDropdownStyle);
});

/**Установка стилей выпадающего списка */
function setDropdownStyle() {
  const rect = $wrapper.value?.getBoundingClientRect();
  if (!rect) return;
  dropdownStyle.value = {
    left: rect.left / rem.value + "rem",
    top: rect.top / rem.value + rect.height / rem.value + 0.5 + "rem",
    width: rect.width / rem.value + "rem",
  };
}
/**Изменить имя параметра */
const updateParameterName = (name: string) => {
  props.upsertParameter.Name = name;
};
/**Изменить цвет параметра */
const updateParameterColor = (color: string) => {
  props.upsertParameter.Color = color;
};
/**Добавление параметра */
const sendParameter = () => {
  if (!props.upsertParameter.Name || isCreateDisabled.value) return;
  emit("upsert", props.upsertParameter);
};
/**Выбор параметра для добавления */
const chooseParameter = (p: TaskParameterModel) => {
  const parameter = new TaskParameterModel(p);
  delete parameter.Id;
  emit("upsert", parameter);
};
/**Установка значения параметра isFocused */
const setIsFocus = (focus: boolean) => {
  isFocused.value = focus;
};
/**Выбор шаблона */
const chooseTemplate = (template: ParametersTemplateModel) => {
  emit("selectTemplate", template);
};

/**Код для использования по ref */
defineExpose({ setDropdownStyle });
</script>
<template>
  <div class="task-parameters-input" ref="$wrapper">
    <TirPmInput
      :model-value="upsertParameter.Name"
      :label="label"
      :isWithHint="false"
      @update:model-value="updateParameterName"
      @enter="sendParameter"
      @focus="setIsFocus(true)"
      @blur="setIsFocus(false)"
    />
    <Transition name="task-parameters-input_animation">
      <div v-if="upsertParameter.Name">
        <TirPmColorPalette
          v-if="type != TaskParameterTypeEnum.Type && !isCreateDisabled"
          class="task-parameters-input_color"
          :model-value="upsertParameter.Color"
          :position="PopoverPositionEnum.Bottom"
          :colors="getTaskParameterPalette(type)"
          @update:model-value="updateParameterColor"
        >
          <template #icon="slot">
            <div class="task-parameters-input_color_icon" @mousedown.stop>
              <PaintIcon :style="{ color: slot.color }" />
            </div>
          </template>
        </TirPmColorPalette>
        <div
          class="task-parameters-input_icon plus"
          :class="addButtonClasses"
          @click="sendParameter"
        >
          <PlusIcon />
        </div>
      </div>
    </Transition>
    <Teleport to="body">
      <Transition name="task-parameters-input_animation">
        <div v-if="isDropdownVisible" class="task-parameters-input_dropdown" :style="dropdownStyle">
          <TirPmScrollbar>
            <div class="task-parameters-input_search">
              <template v-if="searchedTemplates.length && !hideTemplates">
                <div class="task-parameters-input_search_title">Шаблоны:</div>
                <div
                  class="task-parameters-input_search_item template"
                  v-for="template in searchedTemplates"
                  @mousedown.stop
                  @click="chooseTemplate(template)"
                >
                  <div v-text="template.Name" class="task-parameters-input_search_item_template" />
                  <div class="task-parameters-input_search_item_parameters">
                    <TaskParameter
                      v-for="parameter in template.ParsedData"
                      preview
                      :type="type"
                      :data="parameter"
                    />
                  </div>
                </div>
              </template>
              <template v-if="searchedParameters.length">
                <div v-text="`${title} по названию:`" class="task-parameters-input_search_title" />
                <div
                  class="task-parameters-input_search_item"
                  v-for="parameter in searchedParameters"
                  @mousedown.stop
                  @click="chooseParameter(parameter)"
                >
                  <TaskParameter preview :type="type" :data="parameter" />
                </div>
              </template>
            </div>
          </TirPmScrollbar>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>
<style lang="scss" scoped>
.task-parameters-input {
  display: flex;
  gap: 0.5rem;
  position: relative;
  width: 100%;
  & > .input {
    min-width: 100%;
  }
  &_color {
    position: absolute;
    top: 50%;
    right: 2.5rem;
    transform: translateY(-50%);
    &_icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      cursor: pointer;
      border-radius: 0.25rem;
      transition: 0.1s ease;
      &:hover {
        background-color: var(--neutral-translucent-5);
      }
      & > svg {
        width: 1.25rem;
        height: 1.25rem;
      }
    }
  }
  &_icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    cursor: pointer;
    border-radius: 0.25rem;
    transition: 0.1s ease;

    &:hover:not(.disabled) {
      background-color: var(--neutral-translucent-5);
    }
    & > svg {
      width: 1.5rem;
      height: 1.5rem;
    }
    &.plus {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: 0.75rem;
    }
    &.disabled {
      cursor: default;
      opacity: 0.3;
    }
  }
}
</style>
<style lang="scss">
.task-parameters-input_dropdown {
  width: 100%;
  position: absolute;
  background-color: var(--bg-base);
  border-radius: 0.5rem;
  overflow: hidden;
  border: 0.0625rem solid var(--neutral-10, #eaeaeb);
  transition: 0.3s;
  z-index: 10000;
  box-shadow:
    1.1875rem 3.375rem 1rem 0rem rgba(0, 0, 0, 0),
    0.75rem 2.125rem 0.9375rem 0rem rgba(0, 0, 0, 0.01),
    0.4375rem 1.1875rem 0.75rem 0rem rgba(0, 0, 0, 0.05),
    0.1875rem 0.5625rem 0.5625rem 0rem rgba(0, 0, 0, 0.09),
    0.0625rem 0.125rem 0.3125rem 0rem rgba(0, 0, 0, 0.1);
}
.task-parameters-input_search {
  display: flex;
  flex-direction: column;
  max-height: 15.25rem;
  &_item {
    display: flex;
    justify-content: center;
    flex-direction: column;
    gap: 0.25rem;
    width: 100%;
    min-height: 2.75rem;
    padding: 0 0.75rem;
    transition: 0.2s linear;
    cursor: pointer;
    &:hover {
      background: var(--neutral-translucent-5, rgba(39, 45, 55, 0.05));
    }
    &_template {
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--text-secondary, #686c73);
    }
    &_parameters {
      display: flex;
      gap: 0.25rem;
    }
    &.template {
      padding: 0.5rem 0.75rem;
      min-height: unset;
    }
  }
  &_title {
    color: var(--text-primary, #272d37);
    font-size: 1rem;
    font-weight: 500;
    padding: 1.25rem 0 0 0.75rem;
    &:first-child {
      margin-bottom: 0.5rem;
    }
  }
}

.task-parameters-input_animation-enter-active,
.task-parameters-input_animation-leave-active {
  transition: 0.3s;
  opacity: 1;
}
.task-parameters-input_animation-enter-from,
.task-parameters-input_animation-leave-to {
  transition: 0.3s;
  opacity: 0;
}
</style>
